// Home v2 — network filter for the dashboard toolbar. Now an inline dropdown
// (shared NetworkFilterDropdown, same as the Activity screen) instead of
// navigating to the standalone networks route; selecting a network dispatches
// `SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER` so the token list filters by it.
import React, { useCallback, useMemo } from 'react'

import { useTranslation } from '@common/config/localization'
import { isMobileBrowser } from '@common/config/env'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'
import { ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useExtensionAwareNetworks from '@web/hooks/useExtensionAwareNetworks'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { sortNetworksPulseFirst } from '@common/utils/sortNetworks'

import useHomeBreakpoints from '../useHomeBreakpoints'
import NetworkFilterDropdown, { NetworkFilterOption } from './NetworkFilterDropdown'

type Props = {
  currentTab: TabType
}

const NetworkPill: React.FC<Props> = ({ currentTab }) => {
  const { t } = useTranslation()
  const { mode } = useAppMode()
  const { dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { networks } = useExtensionAwareNetworks()
  const { dispatch } = useBackgroundService()
  const { isMobile } = useHomeBreakpoints()

  // On mobile-device browsers, private mode scans one chain at a time (memory),
  // so the "All Networks" option is omitted — see railgunContext clamping.
  const hideAllNetworks = isMobileBrowser && mode === ModeEnum.PRIVATE

  const options: NetworkFilterOption[] = useMemo(
    () => [
      ...(hideAllNetworks ? [] : [{ value: 'all', label: t('All Networks') }]),
      ...sortNetworksPulseFirst(networks).map((n) => ({
        value: n.chainId.toString(),
        label: n.name,
        iconId: n.chainId.toString()
      }))
    ],
    [networks, t, hideAllNetworks]
  )

  const value = dashboardNetworkFilter ? dashboardNetworkFilter.toString() : 'all'

  const onSelect = useCallback(
    (next: string) => {
      const network = networks.find((n) => n.chainId.toString() === next)
      dispatch({
        type: 'SELECTED_ACCOUNT_SET_DASHBOARD_NETWORK_FILTER',
        params: { dashboardNetworkFilter: network ? network.chainId : null }
      })
    },
    [networks, dispatch]
  )

  return (
    <NetworkFilterDropdown
      testID={`networks-dropdown-${currentTab}`}
      options={options}
      value={value}
      onSelect={onSelect}
      compact={isMobile}
    />
  )
}

export default React.memo(NetworkPill)
